/**
 * Server state for the Bridge tab (docs/ux/bridge.md): the view, polled
 * every 1.5 s while a job runs, the package submission, and the two raw-body
 * uploads with progress. A finished step invalidates the hub's detail (the
 * header's sub-state), the workspace (ingest wrote a version) and the
 * queues.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ApiError, api, readActingAs, reasonOf } from '@/app/api';
import type { BridgeTranscriptView, BridgeView } from '@/app/types';
import { detailKey, refreshAudit } from '../useMatter';

export const bridgeKey = (ref: string) => ['matter', ref, 'bridge'] as const;
export const transcriptKey = (ref: string, fileId: string) => ['matter', ref, 'bridge', 'transcript', fileId] as const;

const live = (job: { state: string } | null | undefined): boolean => Boolean(job && (job.state === 'queued' || job.state === 'running'));

/** A job is running: the package, any returned file's ingest (D-0057), or any recording's transcript (D-0088). */
export const bridgeBusy = (view: BridgeView | null | undefined): boolean =>
  Boolean(view && (live(view.packageJob) || live(view.ingest?.job) || (view.returnedFiles ?? []).some((f) => live(f.ingest?.job)) || (view.audioFiles ?? []).some((f) => live(f.transcript?.job))));

/** One string per recording and outcome, so an effect can tell a transcript landing from any other change to the view. */
export const transcriptSignature = (view: BridgeView | null | undefined): string =>
  (view?.audioFiles ?? []).map((f) => `${f.id}:${f.transcript?.job.state ?? '-'}`).join('|');

export function useBridge(ref: string) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: bridgeKey(ref),
    queryFn: async (): Promise<BridgeView | null> => {
      try {
        return await api<BridgeView>(`/api/matters/${encodeURIComponent(ref)}/bridge`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 5_000,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    refetchInterval: (q) => (bridgeBusy(q.state.data) ? 1_500 : false),
  });
  // A step's arrival changes the header's sub-state and, after ingest, the workspace and the queues.
  const lastMode = useRef<string | null>(null);
  useEffect(() => {
    const mode = query.data?.mode ?? null;
    if (lastMode.current !== null && mode !== lastMode.current) {
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      void refreshAudit(client, ref);
      void client.invalidateQueries({ queryKey: ['workspace', ref] });
      void client.invalidateQueries({ queryKey: ['qa'] });
      void client.invalidateQueries({ queryKey: ['dr'] });
    }
    lastMode.current = mode;
  }, [query.data?.mode, client, ref]);
  // A transcript landing or failing leaves the mode alone, so the audit is refreshed on the recordings' outcomes as well.
  const lastTranscripts = useRef<string | null>(null);
  const signature = transcriptSignature(query.data);
  useEffect(() => {
    if (lastTranscripts.current !== null && signature !== lastTranscripts.current) void refreshAudit(client, ref);
    lastTranscripts.current = signature;
  }, [signature, client, ref]);
  return query;
}

/** Ask for a recording's machine transcript (D-0088): 202 with the job; 409 and 422 carry the reason. */
export function useTranscribe(ref: string, onRefused: (reason: string) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api<{ fileId: string; jobId: string; state: 'queued' | 'running' }>(`/api/matters/${encodeURIComponent(ref)}/bridge/transcribe`, { method: 'POST', json: { fileId } }),
    // The job goes onto the row at once, as the package job does, so nothing re-enables in the gap before the refetch lands.
    onSuccess: (started) => {
      client.setQueryData<BridgeView | null>(bridgeKey(ref), (cur) => (cur ? {
        ...cur,
        audioFiles: cur.audioFiles.map((f) => (f.id === started.fileId
          ? { ...f, transcript: { job: { jobId: started.jobId, state: started.state }, fileId: f.id, at: null, key: null, mode: null, code: null, attempt: (f.transcript?.attempt ?? 0) + 1, counts: f.transcript?.counts ?? { chars: 0, words: 0, paragraphs: 0, durationSeconds: null, doubtfulWords: 0, styleChanges: 0 } } }
          : f)),
      } : cur));
      void client.invalidateQueries({ queryKey: bridgeKey(ref) });
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) { onRefused(reasonOf(err) ?? 'The transcription was refused.'); void client.invalidateQueries({ queryKey: bridgeKey(ref) }); return; }
      toast.error('The transcription was not started. Nothing changed.', { action: { label: 'Retry', onClick: () => void client.invalidateQueries({ queryKey: bridgeKey(ref) }) } });
    },
  });
}

/** The transcript text for one recording, once its job succeeded; the object is immutable, so it is never refetched. */
export function useTranscript(ref: string, fileId: string | null) {
  return useQuery({
    queryKey: transcriptKey(ref, fileId ?? ''),
    enabled: fileId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    queryFn: async (): Promise<BridgeTranscriptView | null> => {
      try {
        return await api<BridgeTranscriptView>(`/api/matters/${encodeURIComponent(ref)}/bridge/transcript/${encodeURIComponent(fileId ?? '')}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function usePackage(ref: string, onRefused: (reason: string) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ jobId: string; state: 'queued' | 'running' }>(`/api/matters/${encodeURIComponent(ref)}/bridge/package`, { method: 'POST' }),
    onSuccess: (job) => {
      client.setQueryData<BridgeView | null>(bridgeKey(ref), (cur) => (cur ? { ...cur, packageJob: { jobId: job.jobId, state: job.state } } : cur));
      void client.invalidateQueries({ queryKey: bridgeKey(ref) });
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) { onRefused(reasonOf(err) ?? 'The package was refused.'); void client.invalidateQueries({ queryKey: bridgeKey(ref) }); return; }
      toast.error('The package was not started. Nothing changed.', { action: { label: 'Retry', onClick: () => void client.invalidateQueries({ queryKey: bridgeKey(ref) }) } });
    },
  });
}

export class UploadRefused extends Error {
  readonly status: number;
  readonly reason: string | null;
  constructor(status: number, reason: string | null) {
    super(reason ?? `upload refused (${status})`);
    this.name = 'UploadRefused';
    this.status = status;
    this.reason = reason;
  }
}

/** A raw-body upload with progress; the acting-as header travels as on every request. */
export function uploadBridgeFile(ref: string, which: 'audio' | 'return', body: Blob, contentType: string, onProgress: (percent: number) => void, signal?: AbortSignal, fileName?: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/matters/${encodeURIComponent(ref)}/bridge/${which}`);
    xhr.setRequestHeader('content-type', contentType);
    xhr.setRequestHeader('accept', 'application/json');
    // The file's own name, for the row (D-0057); a recording from the microphone has none.
    if (fileName) xhr.setRequestHeader('x-beam-file-name', encodeURIComponent(fileName));
    const actingAs = readActingAs();
    if (actingAs) xhr.setRequestHeader('x-beam-acting-as', actingAs);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onerror = () => reject(new UploadRefused(0, null));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    xhr.onload = () => {
      let parsed: unknown = null;
      try { parsed = JSON.parse(xhr.responseText); } catch { parsed = null; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
      else {
        const reason = parsed && typeof parsed === 'object' && 'reason' in parsed && typeof (parsed as { reason?: unknown }).reason === 'string' ? (parsed as { reason: string }).reason : null;
        reject(new UploadRefused(xhr.status, reason));
      }
    };
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(body);
  });
}

/** Read the chosen returned file (D-0057): 202 with the job; 409 and 422 carry the reason. */
export function useIngest(ref: string, onRefused: (reason: string) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api<{ fileId: string; jobId: string | null; state: 'queued' | 'running' | null }>(`/api/matters/${encodeURIComponent(ref)}/bridge/ingest`, { method: 'POST', json: { fileId } }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: bridgeKey(ref) }); },
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) { onRefused(reasonOf(err) ?? 'The ingest was refused.'); void client.invalidateQueries({ queryKey: bridgeKey(ref) }); return; }
      toast.error('The ingest was not started. Nothing changed.', { action: { label: 'Retry', onClick: () => void client.invalidateQueries({ queryKey: bridgeKey(ref) }) } });
    },
  });
}

/** Remove a file from the bridge (D-0057): the pointer goes, the object stays as evidence; the view comes back. */
export function useRemoveFile(ref: string, onRefused: (reason: string) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api<BridgeView>(`/api/matters/${encodeURIComponent(ref)}/bridge/files/${encodeURIComponent(fileId)}/remove`, { method: 'POST' }),
    onSuccess: (view) => {
      client.setQueryData<BridgeView | null>(bridgeKey(ref), view);
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      void refreshAudit(client, ref);
      void client.invalidateQueries({ queryKey: ['qa'] });
      void client.invalidateQueries({ queryKey: ['dr'] });
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) { onRefused(reasonOf(err) ?? 'The file was not removed.'); void client.invalidateQueries({ queryKey: bridgeKey(ref) }); return; }
      toast.error('The file was not removed. Nothing changed.', { action: { label: 'Retry', onClick: () => void client.invalidateQueries({ queryKey: bridgeKey(ref) }) } });
    },
  });
}
